import type { TaskCard, TaskPickupSkip, TaskSubtaskWaiting, TaskWaiting, TaskWaitingRef } from '@shared/types/tasks';

/** Waiting on: one key per person, task, the AI builder or upstream (T-58: the task's source). */
export const waitingKey = (w: TaskWaitingRef | TaskWaiting) =>
  w.target === 'user' ? `user:${w.user_id}` : w.target === 'task' ? `task:${w.task_id}` : w.target === 'upstream' ? 'upstream' : 'ai';

/** A card's wait as the API takes it back. */
export const asWaitingRef = (w: TaskWaiting): TaskWaitingRef =>
  w.target === 'user'
    ? { target: 'user', user_id: w.user_id }
    : w.target === 'task'
      ? { target: 'task', task_id: w.task_id }
      : w.target === 'upstream'
        ? { target: 'upstream' }
        : { target: 'ai' };

/** Whether it's this person's turn on the task. */
export function isWaitingOn(waiting: TaskWaiting[], userId: string | null | undefined): boolean {
  return Boolean(userId) && waiting.some((w) => w.target === 'user' && w.user_id === userId);
}

/** T-92: its open sub-tasks waiting on this person (none when it has none, or it's no one). */
export function subtasksWaitingOn(task: Pick<TaskCard, 'subtask_waiting'>, userId: string | null | undefined): TaskSubtaskWaiting['tasks'] {
  if (!userId) return [];
  return (task.subtask_waiting ?? []).find((w) => w.user_id === userId)?.tasks ?? [];
}

/**
 * T-92: a parent's roll-up once one of its sub-tasks changes: the sub-task is taken out of every
 * person's list, then put back under each person it now waits on, if it's still open (not done, not
 * archived). What the server works out in CARD_SELECT, for showing the change before it answers.
 */
export function rollUpSubtaskWait(
  rollUp: TaskSubtaskWaiting[],
  sub: { task_id: string; number: number },
  open: boolean,
  waiting: TaskWaiting[]
): TaskSubtaskWaiting[] {
  const next = new Map(rollUp.map((w) => [w.user_id, w.tasks.filter((t) => t.task_id !== sub.task_id)]));
  if (open) {
    for (const w of waiting) {
      if (w.target !== 'user') continue;
      const tasks = next.get(w.user_id) ?? [];
      if (!tasks.some((t) => t.task_id === sub.task_id)) next.set(w.user_id, [...tasks, { task_id: sub.task_id, number: sub.number }].sort((a, b) => a.number - b.number));
    }
  }
  return [...next]
    .filter(([, tasks]) => tasks.length > 0)
    .map(([user_id, tasks]) => ({ user_id, tasks }))
    .sort((a, b) => a.tasks[0].number - b.tasks[0].number);
}

/** The reason on a task waiting on the AI builder, or null. */
export function pickupSkipOf(waiting: TaskWaiting[]): TaskPickupSkip | null {
  for (const w of waiting) if (w.target === 'ai' && w.pickup_skip) return w.pickup_skip;
  return null;
}

/** "Every machine is busy (…)." from the stored clause. */
export function pickupSkipSentence(skip: TaskPickupSkip): string {
  return reasonSentence(skip.reason);
}

/** A stored clause as a sentence: capitalised, with a full stop. */
export function reasonSentence(reason: string): string {
  const r = reason.trim();
  return `${r.charAt(0).toUpperCase()}${r.slice(1)}${/[.!?]$/.test(r) ? '' : '.'}`;
}
